/**
 * App Routes
 * Main routing configuration with lazy loading
 */

import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PATHS } from './paths';
import { ProtectedRoute } from './ProtectedRoute';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

// ===========================================
// Loading Fallback
// ===========================================

function PageLoader() {
    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
    );
}

// ===========================================
// Lazy Loaded Pages - Public
// ===========================================

const HomePage = lazy(() => import('@/pages/public/HomePage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const BusinessCardPage = lazy(() => import('@/pages/public/BusinessCardPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/public/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/public/TermsOfServicePage'));

// Services
const ServicesPage = lazy(() => import('@/pages/public/ServicesPage'));

// Properties
const PropertiesForSalePage = lazy(() => import('@/pages/public/PropertiesForSalePage'));
const PropertyDetailPage = lazy(() => import('@/pages/public/PropertyDetailPage'));

// Open House
const OpenHousePage = lazy(() => import('@/pages/public/OpenHousePage'));
// Visitor sign-in sheet (kiosk / phone). Chrome-free like the meeting room.
const OpenHouseSignInPage = lazy(() => import('@/pages/public/OpenHouseSignInPage'));
const OpenHouseBusinessCardPage = lazy(() => import('@/pages/public/OpenHouseBusinessCardPage'));
const ChatVerifyEmailPage = lazy(() => import('@/pages/public/ChatVerifyEmailPage'));

// Articles published from the social editor (/manage/social)
const PostsListPage = lazy(() => import('@/pages/public/PostsListPage'));
const PostDetailPage = lazy(() => import('@/pages/public/PostDetailPage'));

// Agreements
const OurAgreementsPage = lazy(() => import('@/pages/public/OurAgreementsPage'));
const AgreementSignupPage = lazy(() => import('@/pages/public/AgreementSignupPage'));
const BuyersAgreementReviewPage = lazy(() => import('@/pages/public/BuyersAgreementReviewPage'));
const AgreementApprovePage = lazy(() => import('@/pages/public/AgreementApprovePage'));

// Tools
const MortgageCalculatorPage = lazy(() => import('@/pages/public/MortgageCalculatorPage'));
const SavedSearchPage = lazy(() => import('@/pages/public/SavedSearchPage'));
const HomeValuePage = lazy(() => import('@/pages/public/HomeValuePage'));
const HomeValueConfirmPage = lazy(() => import('@/pages/public/HomeValueConfirmPage'));
const HomeValueReportPage = lazy(() => import('@/pages/public/HomeValueReportPage'));

// ===========================================
// Lazy Loaded Pages - Auth
// ===========================================

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));

// ===========================================
// Lazy Loaded Pages - User (Protected)
// ===========================================

const ProfilePage = lazy(() => import('@/pages/user/ProfilePage'));
const BookAppointmentPage = lazy(() => import('@/pages/user/BookAppointmentPage'));

// ===========================================
// Lazy Loaded Pages - Admin (Protected)
// ===========================================

const AdminDashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const AdminContactsPage = lazy(() => import('@/pages/admin/ContactsPage'));
const AdminCompaniesPage = lazy(() => import('@/pages/admin/CompaniesPage'));
const AdminCompanyDetailPage = lazy(() => import('@/pages/admin/CompanyDetailPage'));
const AdminMeetingsPage = lazy(() => import('@/pages/admin/MeetingsPage'));
const AdminRoomsPage = lazy(() => import('@/pages/admin/RoomsPage'));
const AdminRoomDetailPage = lazy(() => import('@/pages/admin/RoomDetailPage'));
const AdminArchivePage = lazy(() => import('@/pages/admin/ArchivePage'));
const AdminTestRunsPage = lazy(() => import('@/pages/admin/TestRunsPage'));
const AdminTestRunDetailPage = lazy(() => import('@/pages/admin/TestRunDetailPage'));
const AdminProblemReportsPage = lazy(() => import('@/pages/admin/ProblemReportsPage'));
const AdminIncidentsPage = lazy(() => import('@/pages/admin/IncidentsPage'));
const AdminIncidentDetailPage = lazy(() => import('@/pages/admin/IncidentDetailPage'));
const MeetingRoomPage = lazy(() => import('@/pages/public/MeetingRoomPage'));
const AdminContactDetailPage = lazy(() => import('@/pages/admin/ContactDetailPage'));
const AdminContactFormPage = lazy(() => import('@/pages/admin/ContactFormPage'));
const AdminPropertiesPage = lazy(() => import('@/pages/admin/PropertiesPage'));
const AdminPropertyAddPage = lazy(() => import('@/pages/admin/PropertyAddPage'));
const AdminScrapeZillowPage = lazy(() => import('@/pages/admin/ScrapeZillowPage'));
const AdminPropertyDetailPage = lazy(() => import('@/pages/admin/PropertyDetailPage'));
const AdminOpenHouseCreatePage = lazy(() => import('@/pages/admin/OpenHouseCreatePage'));
const AdminOpenHouseDetailPage = lazy(() => import('@/pages/admin/OpenHouseDetailPage'));
const AdminOpenHousePrintPage = lazy(() => import('@/pages/admin/OpenHousePrintPage'));
const AdminAgreementsPage = lazy(() => import('@/pages/admin/AgreementsPage'));
const AdminAffiliatesPage = lazy(() => import('@/pages/admin/AffiliatesPage'));
const AdminCommunicationPage = lazy(() => import('@/pages/admin/CommunicationPage'));
const AdminAutoReplyExclusionsPage = lazy(() => import('@/pages/admin/AutoReplyExclusionsPage'));
const AdminCallHistoryPage = lazy(() => import('@/pages/admin/CallHistoryPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));
const AdminSecurityPage = lazy(() => import('@/pages/admin/SecurityPage'));
const AdminMailPage = lazy(() => import('@/pages/admin/MailPage'));
const AdminBinPage = lazy(() => import('@/pages/admin/BinPage'));
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'));
const AdminLicenseCheckPage = lazy(() => import('@/pages/admin/LicenseCheckPage'));
const AdminAiSettingsPage = lazy(() => import('@/pages/admin/AiSettingsPage'));
const AdminLivekitSettingsPage = lazy(() => import('@/pages/admin/LivekitSettingsPage'));
const AdminEmailBlacklistPage = lazy(() => import('@/pages/admin/EmailBlacklistPage'));
const AdminScraperSettingsPage = lazy(() => import('@/pages/admin/ScraperSettingsPage'));
const AdminHomeValuesPage = lazy(() => import('@/pages/admin/HomeValuesPage'));
const AdminRecentSalesPage = lazy(() => import('@/pages/admin/RecentSalesPage'));
const AdminValuationSourcesPage = lazy(() => import('@/pages/admin/ValuationSourcesPage'));
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/IntegrationsPage'));
const AdminBrandsPage = lazy(() => import('@/pages/admin/BrandsPage'));
const AdminSetupWizard = lazy(() => import('@/pages/admin/SetupWizard'));
const AdminCompliancePage = lazy(() => import('@/pages/admin/CompliancePage'));
const AdminAiChatSessionsPage = lazy(() => import('@/pages/admin/AiChatSessionsPage'));
const AdminSponsorsPage = lazy(() => import('@/pages/admin/SponsorsPage'));
const PublicLenderPage = lazy(() => import('@/pages/public/LenderPage'));
const QrApprovePage = lazy(() => import('@/pages/auth/QrApprovePage'));
const ShareCardPage = lazy(() => import('@/pages/public/ShareCardPage'));
const AdminMeCardPage = lazy(() => import('@/pages/admin/MeCardPage'));
const AdminPostsPage = lazy(() => import('@/pages/admin/PostsPage'));
const AdminPostEditorPage = lazy(() => import('@/pages/admin/PostEditorPage'));
const AdminSocialAnalyticsPage = lazy(() => import('@/pages/admin/SocialAnalyticsPage'));
const SponsorLoginPage = lazy(() => import('@/pages/sponsor/SponsorLoginPage'));
const SponsorApplyPage = lazy(() => import('@/pages/sponsor/SponsorApplyPage'));
const SponsorSubscribePage = lazy(() => import('@/pages/sponsor/SponsorSubscribePage'));
const SponsorPortalPage = lazy(() => import('@/pages/sponsor/SponsorPortalPage'));
const SponsorCheckoutSuccessPage = lazy(() =>
    import('@/pages/sponsor/SponsorCheckoutResultPage').then((m) => ({ default: m.SponsorCheckoutSuccessPage })),
);
const SponsorCheckoutCancelPage = lazy(() =>
    import('@/pages/sponsor/SponsorCheckoutResultPage').then((m) => ({ default: m.SponsorCheckoutCancelPage })),
);

// ===========================================
// Layouts
// ===========================================

const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const AdminLayout = lazy(() => import('@/layouts/AdminLayout'));

// ===========================================
// Not Found
// ===========================================

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// ===========================================
// Routes Component
// ===========================================

export function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
            <ScrollToTop />
            <Routes>
                {/* ========================= */}
                {/* Public Routes */}
                {/* ========================= */}
                <Route element={<MainLayout />}>
                    <Route path={PATHS.HOME} element={<HomePage />} />
                    <Route path={PATHS.ABOUT} element={<AboutPage />} />
                    {/* /contact renders the flippable business card (the
                        previous /contact lead-capture form had broken
                        backend wiring — "could not load the agent
                        profile" — and is no longer routed; the file
                        stays in the repo for future restoration). */}
                    <Route path={PATHS.CONTACT} element={<BusinessCardPage />} />
                    {/* Redirect the old /business-card URL so printed
                        cards / QR codes / shared links still work. */}
                    <Route
                        path={PATHS.BUSINESS_CARD}
                        element={<Navigate to={PATHS.CONTACT} replace />}
                    />
                    {/* Standalone preview of the Twilio-OTP lead-capture
                        page. Reachable at /contact-form so the operator
                        can review it independent of the public /contact
                        business-card view. */}
                    <Route path={PATHS.CONTACT_FORM} element={<ContactPage />} />
                    <Route path={PATHS.PRIVACY} element={<PrivacyPolicyPage />} />
                    <Route path={PATHS.TERMS} element={<TermsOfServicePage />} />

                    {/* Services */}
                    <Route path={PATHS.SERVICES} element={<ServicesPage />} />

                    {/* Properties */}
                    <Route path={PATHS.REALESTATE} element={<PropertiesForSalePage />} />
                    {/* Legacy URLs — redirect bookmarks and any stale
                        external links to the new canonical path. */}
                    <Route
                        path={PATHS.PROPERTIES}
                        element={<Navigate to={PATHS.REALESTATE} replace />}
                    />
                    <Route
                        path={PATHS.PROPERTIES_FOR_SALE}
                        element={<Navigate to={PATHS.REALESTATE} replace />}
                    />
                    <Route path={PATHS.PROPERTY_DETAIL} element={<PropertyDetailPage />} />

                    {/* Open House */}
                    <Route path={PATHS.OPEN_HOUSE} element={<OpenHousePage />} />
                    {/* The old placeholder registration form is gone —
                        visitors sign in per open house through the QR /
                        kiosk link, so stale bookmarks land on the list. */}
                    <Route
                        path={PATHS.OPEN_HOUSE_REGISTER}
                        element={<Navigate to={PATHS.OPEN_HOUSE} replace />}
                    />

                    {/* Articles — the pages the social posts link back to */}
                    <Route path={PATHS.POSTS} element={<PostsListPage />} />
                    <Route path={PATHS.POST_DETAIL} element={<PostDetailPage />} />

                    {/* Chat email-link verification (international 2FA) */}
                    <Route path={PATHS.CHAT_VERIFY_EMAIL} element={<ChatVerifyEmailPage />} />
                    {/* Sponsor self-serve portal — magic-link auth (no password).
                        The login page emails a single-use token; the portal
                        page reads ?token= and rotates it on every backend call. */}
                    <Route path={PATHS.LENDER_PUBLIC} element={<PublicLenderPage />} />
                    <Route path={PATHS.QR_APPROVE} element={<QrApprovePage />} />
                    <Route path={PATHS.SHARE_CARD} element={<ShareCardPage />} />
                    <Route path={PATHS.SPONSOR_LOGIN} element={<SponsorLoginPage />} />
                    <Route path={PATHS.SPONSOR_APPLY} element={<SponsorApplyPage />} />
                    <Route path={PATHS.SPONSOR_SUBSCRIBE} element={<SponsorSubscribePage />} />
                    <Route path={PATHS.SPONSOR_PORTAL} element={<SponsorPortalPage />} />
                    <Route path={PATHS.SPONSOR_CHECKOUT_SUCCESS} element={<SponsorCheckoutSuccessPage />} />
                    <Route path={PATHS.SPONSOR_CHECKOUT_CANCEL} element={<SponsorCheckoutCancelPage />} />

                    {/* Agreements */}
                    <Route path={PATHS.OUR_AGREEMENTS} element={<OurAgreementsPage />} />
                    <Route path={PATHS.AGREEMENT_SIGNUP} element={<AgreementSignupPage />} />
                    <Route
                        path={PATHS.BUYERS_AGREEMENT_REVIEW}
                        element={
                            <ProtectedRoute>
                                <BuyersAgreementReviewPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path={PATHS.AGREEMENT_APPROVE} element={<AgreementApprovePage />} />

                    {/* Tools */}
                    <Route path={PATHS.MORTGAGE_CALCULATOR} element={<MortgageCalculatorPage />} />
                    <Route path={PATHS.SAVED_SEARCH} element={<SavedSearchPage />} />
                    <Route path={PATHS.HOME_VALUE} element={<HomeValuePage />} />
                    <Route path={PATHS.HOME_VALUE_CONFIRM} element={<HomeValueConfirmPage />} />
                    <Route path={PATHS.HOME_VALUE_REPORT} element={<HomeValueReportPage />} />

                    {/* User Protected Routes */}
                    <Route
                        path={PATHS.PROFILE}
                        element={
                            <ProtectedRoute>
                                <ProfilePage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Public: visitors book without an account; the
                        operator confirms on /manage/meetings. */}
                    <Route path={PATHS.BOOK_APPOINTMENT} element={<BookAppointmentPage />} />

                    {/* Auth Routes - inside MainLayout for header/footer */}
                    <Route path={PATHS.AUTH.LOGIN} element={<LoginPage />} />
                    <Route path={PATHS.AUTH.SIGNUP} element={<SignupPage />} />
                    <Route path={PATHS.AUTH.VERIFY_EMAIL} element={<VerifyEmailPage />} />
                </Route>

                {/* Public meeting room — full-screen, no MainLayout
                    chrome (no header / footer / chat widget). Anyone
                    with the link can land here; the room is gated by
                    the LiveKit access token issued by the backend's
                    /meetings/<code>/join endpoint. */}
                <Route path={PATHS.MEETING_ROOM} element={<MeetingRoomPage />} />

                {/* Open house sign-in sheet — full-screen kiosk / phone
                    view with no header, footer or chat widget. `:code`
                    maps to open_houses.public_code; the backend decides
                    whether the sheet is still open (410 after close). */}
                <Route path={PATHS.OPEN_HOUSE_SIGN_IN} element={<OpenHouseSignInPage />} />
                <Route path={PATHS.OPEN_HOUSE_BUSINESS_CARD} element={<OpenHouseBusinessCardPage />} />

                {/* ========================= */}
                {/* Admin Routes */}
                {/* ========================= */}
                <Route path={PATHS.ADMIN.LOGIN} element={<LoginPage />} />
                <Route path={PATHS.ADMIN.REGISTER} element={<Navigate to={PATHS.AUTH.SIGNUP} replace />} />

                <Route
                    element={
                        <ProtectedRoute requiredRole="admin">
                            <AdminLayout />
                        </ProtectedRoute>
                    }
                >
                    {/* Admin Root Redirect */}
                    <Route path={PATHS.ADMIN.ROOT} element={<Navigate to={PATHS.ADMIN.DASHBOARD} replace />} />

                    {/* Dashboard */}
                    <Route path={PATHS.ADMIN.DASHBOARD} element={<AdminDashboardPage />} />

                    {/* Contacts */}
                    <Route path={PATHS.ADMIN.CONTACTS} element={<AdminContactsPage />} />
                    <Route path={PATHS.ADMIN.CONTACT_ADD} element={<AdminContactFormPage />} />
                    <Route path={PATHS.ADMIN.CONTACT_DETAIL} element={<AdminContactDetailPage />} />
                    <Route path={PATHS.ADMIN.CONTACT_EDIT} element={<AdminContactFormPage />} />

                    {/* Companies + org-chart view */}
                    <Route path={PATHS.ADMIN.COMPANIES} element={<AdminCompaniesPage />} />
                    <Route path={PATHS.ADMIN.COMPANY_DETAIL} element={<AdminCompanyDetailPage />} />

                    {/* Video / audio meetings (LiveKit) */}
                    <Route path={PATHS.ADMIN.MEETINGS} element={<AdminMeetingsPage />} />
                    <Route path={PATHS.ADMIN.ROOMS} element={<AdminRoomsPage />} />
                    <Route path={PATHS.ADMIN.ROOM_DETAIL} element={<AdminRoomDetailPage />} />
                    <Route path={PATHS.ADMIN.ARCHIVE} element={<AdminArchivePage />} />
                    <Route path={PATHS.ADMIN.TEST_RUNS} element={<AdminTestRunsPage />} />
                    <Route path={PATHS.ADMIN.TEST_RUN_DETAIL} element={<AdminTestRunDetailPage />} />
                    <Route path={PATHS.ADMIN.PROBLEM_REPORTS} element={<AdminProblemReportsPage />} />
                    <Route path={PATHS.ADMIN.INCIDENTS} element={<AdminIncidentsPage />} />
                    <Route path={PATHS.ADMIN.INCIDENT_DETAIL} element={<AdminIncidentDetailPage />} />

                    {/* Properties */}
                    <Route path={PATHS.ADMIN.PROPERTIES} element={<AdminPropertiesPage />} />
                    <Route path={PATHS.ADMIN.PROPERTY_ADD} element={<AdminPropertyAddPage />} />
                    <Route path={PATHS.ADMIN.PROPERTY_SCRAPE} element={<AdminScrapeZillowPage />} />
                    <Route path={PATHS.ADMIN.PROPERTY_DETAIL} element={<AdminPropertyDetailPage />} />

                    {/* Open houses — the list is the Properties page tab */}
                    <Route path={PATHS.ADMIN.OPEN_HOUSE_NEW} element={<AdminOpenHouseCreatePage />} />
                    <Route path={PATHS.ADMIN.OPEN_HOUSE_DETAIL} element={<AdminOpenHouseDetailPage />} />
                    <Route path={PATHS.ADMIN.OPEN_HOUSE_PRINT} element={<AdminOpenHousePrintPage />} />

                    {/* Other Admin Pages */}
                    <Route path={PATHS.ADMIN.AGREEMENTS} element={<AdminAgreementsPage />} />
                    <Route path={PATHS.ADMIN.AFFILIATES} element={<AdminAffiliatesPage />} />
                    <Route path={PATHS.ADMIN.COMMUNICATION} element={<AdminCommunicationPage />} />
                    <Route path={PATHS.ADMIN.AUTO_REPLY_EXCLUSIONS} element={<AdminAutoReplyExclusionsPage />} />
                    <Route path={PATHS.ADMIN.CALL_HISTORY} element={<AdminCallHistoryPage />} />
                    <Route path={PATHS.ADMIN.SETTINGS} element={<AdminSettingsPage />} />
                    <Route path={PATHS.ADMIN.SECURITY} element={<AdminSecurityPage />} />
                    <Route path={PATHS.ADMIN.MAIL} element={<AdminMailPage />} />
                    <Route path={PATHS.ADMIN.BIN} element={<AdminBinPage />} />
                    <Route path={PATHS.ADMIN.AUDIT_LOGS} element={<AdminAuditLogsPage />} />
                    <Route path={PATHS.ADMIN.LICENSE_CHECK} element={<AdminLicenseCheckPage />} />
                    <Route path={PATHS.ADMIN.AI_SETTINGS} element={<AdminAiSettingsPage />} />
                    <Route path={PATHS.ADMIN.LIVEKIT_SETTINGS} element={<AdminLivekitSettingsPage />} />
                    <Route path={PATHS.ADMIN.EMAIL_BLACKLIST} element={<AdminEmailBlacklistPage />} />
                    <Route path={PATHS.ADMIN.SCRAPER_SETTINGS} element={<AdminScraperSettingsPage />} />
                    <Route path={PATHS.ADMIN.HOME_VALUES} element={<AdminHomeValuesPage />} />
                    <Route path={PATHS.ADMIN.RECENT_SALES} element={<AdminRecentSalesPage />} />
                    <Route path={PATHS.ADMIN.VALUATION_SOURCES} element={<AdminValuationSourcesPage />} />
                    <Route path={PATHS.ADMIN.INTEGRATIONS} element={<AdminIntegrationsPage />} />
                    <Route path={PATHS.ADMIN.BRANDS} element={<AdminBrandsPage />} />
                    <Route path={PATHS.ADMIN.SETUP} element={<AdminSetupWizard />} />
                    <Route path={PATHS.ADMIN.COMPLIANCE} element={<AdminCompliancePage />} />
                    <Route path={PATHS.ADMIN.SPONSORS} element={<AdminSponsorsPage />} />
                    <Route path={PATHS.ADMIN.ME_CARD} element={<AdminMeCardPage />} />
                    <Route path={PATHS.ADMIN.AI_CHAT_SESSIONS} element={<AdminAiChatSessionsPage />} />

                    {/* Social publishing */}
                    <Route path={PATHS.ADMIN.POSTS} element={<AdminPostsPage />} />
                    <Route path={PATHS.ADMIN.POST_NEW} element={<AdminPostEditorPage />} />
                    <Route path={PATHS.ADMIN.SOCIAL_ANALYTICS} element={<AdminSocialAnalyticsPage />} />
                    <Route path={PATHS.ADMIN.POST_EDIT} element={<AdminPostEditorPage />} />
                </Route>

                {/* ========================= */}
                {/* 404 Not Found */}
                {/* ========================= */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
    );
}

export default AppRoutes;
